// "What's included" — renders the current product's native bundle components
// (with quantity) by reading them from the Storefront GraphQL API. Fixed-bundle
// components aren't exposed in Liquid, so this is the supported way to list them.
//
// Components are per-variant, so the list is repainted whenever the shopper
// picks another variant. The element carries data-skip-subtree-update, which
// means the section morph that follows a variant change leaves its contents
// alone — nothing else would refresh it.

import { ThemeEvents } from '@theme/events';

const QUERY = `
  query Components($handle: String!) {
    product(handle: $handle) {
      variants(first: 20) {
        nodes {
          id
          components(first: 50) {
            nodes {
              quantity
              productVariant {
                id
                title
                price { amount currencyCode }
                image { url altText }
                product { title onlineStoreUrl featuredImage { url altText } }
              }
            }
          }
        }
      }
    }
  }
`;

class WhatsIncluded extends HTMLElement {
  /** Fetches in flight or already resolved, keyed by product handle. */
  #requests = new Map();

  /** Bumped on every render so a slow fetch can't overwrite a newer one. */
  #renderToken = 0;

  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    if (this.dataset.hydrated) return;
    this.dataset.hydrated = 'true';
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  get config() {
    return {
      handle: this.dataset.handle,
      token: this.dataset.token,
      apiVersion: this.dataset.apiVersion || '2025-01',
      variantId: this.dataset.variantId,
      showQuantity: this.dataset.showQuantity === 'true',
      showWorth: this.dataset.showWorth === 'true',
    };
  }

  /**
   * Repoint the element at the newly selected variant and repaint.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const detail = event.detail ?? {};
    const newProduct = detail.data?.newProduct;

    if (newProduct?.url) {
      // Combined listing — the picker swapped in a different product, so both
      // the handle and the product id change under us.
      const handle = this.#handleFromUrl(newProduct.url);
      if (!handle) return;
      this.dataset.handle = handle;
      this.dataset.productId = String(newProduct.id ?? '');
    } else if (String(detail.data?.productId ?? '') !== String(this.dataset.productId ?? '')) {
      // Another product's picker (a quick-add drawer, a second PDP block).
      return;
    }

    const variantId = detail.resource?.id;
    if (variantId == null) return;
    this.dataset.variantId = String(variantId);

    this.render();
  };

  /** @param {string} url @returns {string | null} */
  #handleFromUrl(url) {
    try {
      const path = new URL(url, window.location.origin).pathname;
      return path.split('/products/')[1]?.split(/[/?#]/)[0] || null;
    } catch {
      return null;
    }
  }

  async render() {
    const { handle, token } = this.config;
    const list = this.querySelector('[data-wi-list]');
    if (!handle || !token || !list) return;

    const token_ = ++this.#renderToken;
    const variants = await this.#variants(handle);
    // A newer variant selection landed while this was in flight.
    if (token_ !== this.#renderToken) return;

    const components = variants?.length ? this.componentsForVariant(variants) : [];
    if (components.length === 0) {
      // Hidden rather than removed: a sibling variant may well be a bundle even
      // when this one isn't, and the element has to survive to show it.
      list.replaceChildren();
      this.setAttribute('hidden', '');
      return;
    }

    list.innerHTML = components.map((c) => this.itemHTML(c)).join('');
    this.removeAttribute('hidden');
  }

  /**
   * Every variant's components in one request, cached per handle — switching
   * variants then costs nothing.
   * @param {string} handle
   */
  #variants(handle) {
    let request = this.#requests.get(handle);
    if (!request) {
      request = this.#fetchVariants(handle);
      this.#requests.set(handle, request);
    }
    return request;
  }

  /** @param {string} handle */
  async #fetchVariants(handle) {
    const { token, apiVersion } = this.config;
    try {
      // Same-origin request — no CORS. The Storefront token is public by design.
      // The Storefront API is always at the domain root, not under a locale path.
      const res = await fetch(`/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: QUERY, variables: { handle } }),
      });
      const json = await res.json();
      return json?.data?.product?.variants?.nodes ?? null;
    } catch (e) {
      return null;
    }
  }

  // Pick the components for the currently-selected variant. A matched variant is
  // authoritative: if it has no components it simply isn't a bundle, even when a
  // sibling variant is. The fallback only covers a variant missing from the list.
  componentsForVariant(variants) {
    const { variantId } = this.config;
    const match =
      variantId &&
      variants.find((v) => v.id === variantId || String(v.id).endsWith(`/${variantId}`));
    const chosen = match || variants.find((v) => v.components?.nodes?.length) || variants[0];
    return chosen?.components?.nodes || [];
  }

  itemHTML({ quantity, productVariant }) {
    const { showQuantity, showWorth } = this.config;
    const product = productVariant?.product || {};
    const title = product.title || productVariant?.title || '';
    const url = product.onlineStoreUrl;
    const img = productVariant?.image?.url || product.featuredImage?.url;
    const alt = productVariant?.image?.altText || product.featuredImage?.altText || title;

    const media = img
      ? `<div class="wi__media">
           <img class="wi__img" src="${this.resize(img, 120)}" alt="${this.esc(alt)}" loading="lazy" width="56">
           ${showQuantity && quantity > 1 ? `<span class="wi__qty-badge" aria-hidden="true">×${quantity}</span>` : ''}
         </div>`
      : '';

    const titleEl = url
      ? `<a class="wi__title" href="${this.esc(url)}">${this.esc(title)}</a>`
      : `<span class="wi__title">${this.esc(title)}</span>`;

    // "Worth £X" — the component's own price × its quantity in the bundle.
    const worth =
      showWorth && productVariant?.price
        ? `<span class="wi__worth">Worth ${this.money(productVariant.price, quantity)}</span>`
        : '';

    return `<li class="wi__item">${media}<div class="wi__info">${titleEl}${worth}</div></li>`;
  }

  // Ask Shopify's CDN for a smaller image.
  resize(url, width) {
    try {
      const u = new URL(url);
      u.searchParams.set('width', width);
      return u.toString();
    } catch {
      return url;
    }
  }

  money({ amount, currencyCode }, quantity = 1) {
    const value = parseFloat(amount) * quantity;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode,
      }).format(value);
    } catch {
      return `${value.toFixed(2)} ${currencyCode}`;
    }
  }

  esc(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('whats-included')) {
  customElements.define('whats-included', WhatsIncluded);
}
